import { mediaRef } from '@/services/mediaAssets';

const coverImage = mediaRef('adventures/holiday-roses-luxury-2025.png');
const heroImage = mediaRef('adventures/reckon-ball-2025-roses-no-text.png');

export default {
  slug: '2025',
  year: 2025,
  title: "Reckon Ball at Rose's Luxury",
  theme: 'Resilience',
  coverImage,
  location: { city: 'Washington', state: 'D.C.' },
  hero: {
    eyebrow: 'Reckon Ball 2025',
    title: 'Resilience',
    subtitle: "Waldorf Astoria Washington DC and Rose's Luxury",
    summary: 'Held during the uncertainty of a government shutdown, the 2025 Reckon Ball preserved the traditions that mattered most with a thoughtful evening of private dining, a shared first, karaoke, and brunch together the next morning.',
    image: heroImage,
    imageAlt: "The Reckon Crew gathered for a private holiday dinner at Rose's Luxury in Washington, D.C.",
  },
  hotel: {
    name: 'Waldorf Astoria Washington DC',
    location: 'Washington, D.C.',
    overnightStay: true,
    summary: 'The Waldorf Astoria provided a refined home base for a more thoughtful Reckon Ball held during the uncertainty of a government shutdown.',
    brunch: { venue: 'Waldorf Astoria Washington DC', summary: 'The group gathered for brunch at the hotel the following morning.' },
  },
  timeline: [
    { title: 'Check-In', location: 'Waldorf Astoria Washington DC' },
    { title: 'Private Cocktail Reception', location: "Rose's Luxury", description: 'The entire upstairs was reserved exclusively for the Reckon Crew and their guests.' },
    { title: 'Gift Card Exchange', location: "Rose's Luxury" },
    { title: 'Private Dinner', location: "Rose's Luxury" },
    { title: 'Louis XIII Toast', location: "Rose's Luxury" },
    { title: 'Karaoke', location: "Lola's" },
    { title: 'Overnight Stay', location: 'Waldorf Astoria Washington DC' },
    { title: 'Farewell Brunch', day: 'The Next Morning', location: 'Waldorf Astoria Washington DC' },
  ],
  experiences: [
    {
      id: 'cocktail-reception', eyebrow: 'The Evening Begins', title: 'Private Cocktail Reception', venue: "Rose's Luxury", summary: 'The entire upstairs was reserved exclusively for the Reckon Crew and their guests.',
    },
    {
      id: 'gift-card-exchange', eyebrow: 'Tradition, Adapted', title: 'Gift Card Exchange', venue: "Rose's Luxury", summary: 'A more restrained version of the annual gift exchange reflected the uncertainty of the government shutdown while preserving the tradition.',
    },
    {
      id: 'karaoke', eyebrow: 'After Hours', title: 'Karaoke', venue: "Lola's", summary: 'The celebration continued with karaoke after dinner.',
    },
    {
      id: 'brunch', eyebrow: 'The Next Morning', title: 'Farewell Brunch', venue: 'Waldorf Astoria Washington DC', summary: 'The group gathered for brunch at the hotel the following morning.',
    },
  ],
  traditions: [
    {
      type: 'gift-card-exchange', title: 'Gift Card Exchange', firstYear: 2022, summary: 'A more restrained exchange preserved the tradition during an uncertain year.',
    },
    { type: 'overnight-stay', title: 'Overnight Stay', firstYear: 2022 },
    { type: 'farewell-brunch', title: 'Farewell Brunch', firstYear: 2024 },
    { type: 'signature-toast', title: 'Signature Toast' },
  ],
  dining: {
    restaurant: "Rose's Luxury",
    location: 'Washington, D.C.',
    privateDining: true,
    exclusiveSpace: 'Entire upstairs',
    summary: 'The private upstairs provided an intimate setting for cocktail hour, the gift card exchange, dinner, and a shared Louis XIII toast.',
    menu: {
      eventTitle: "Reckon Ball at Rose's Luxury",
      year: 2025,
      restaurant: "Rose's Luxury",
      location: 'Washington, D.C.',
      sections: [
        {
          title: 'Private Cocktail Reception',
          serviceStyle: 'passed',
          items: [
            { name: "This Afternoon's Ricotta and Focaccia", description: 'Housemade focaccia, available with or without truffle' },
            { name: 'Pickle-Brined Fried Chicken', description: 'Honey and benne seed' },
            { name: 'Solid Wiggle', description: 'An elegant jello shot cocktail', premium: true },
          ],
        },
        { title: 'To Begin', serviceStyle: 'shared', items: [{ name: "Rose's Bread Service" }, { name: 'Baked-to-Order Madeleines' }] },
        { title: 'Plated Courses', items: [{ name: 'Ice Cream, Caviar and Happiness', description: 'Caviar served with ice cream' }, { name: 'Pork and Lychee Salad', description: "Rose's signature pork and lychee salad" }] },
        { title: 'Shared Courses', serviceStyle: 'shared', items: [{ name: 'Italian Beef Sandwich Pasta', description: 'An Italian beef sandwich-inspired pasta dish' }, { name: 'Sungold Tomato Tteokbokki', description: 'Korean rice cakes with spicy Sungold tomato sauce' }] },
        { title: 'Side', items: [{ name: 'Jumbo Green Asparagus', description: 'Pineapple aioli and fried jalapenos' }] },
        {
          title: 'Main Courses',
          serviceStyle: 'choice',
          items: [
            { name: 'Miso-Glazed Cod', description: 'Inspired by Chefs Nobu and Joel Robuchon' },
            { name: 'The Adult Happy Meal', description: 'Wagyu burger, fries dusted with Hidden Valley Ranch seasoning, and a stiff drink' },
            { name: 'Deep-Fried Pork', description: 'Soubise, bechamel and Crystal hot sauce' },
          ],
        },
        { title: 'Dessert', serviceStyle: 'choice', items: [{ name: 'Mole Negro Sticky Toffee Pudding', description: 'Horchata ice cream and toasted peanuts' }, { name: 'Cornbread Fried Ice Cream', description: 'Served with pieces of buttermilk pie' }] },
        { title: 'Wine Pairing', serviceStyle: 'pairing', items: [{ name: 'Super Luxe Wine Pairing' }] },
        { title: 'Signature Experience', items: [{ name: 'Louis XIII Cognac', premium: true }] },
      ],
    },
  },
  signatureExperience: {
    eyebrow: 'A Shared First',
    title: 'Louis XIII Cognac',
    summary: "Knowing that the tasting menu, wine pairings, and open bar would remain well below the private dining minimum, the team planned one additional experience from the beginning. Rose's Luxury sourced a bottle of Louis XIII Cognac so everyone could share a taste together.",
    quote: 'Some experiences are measured by their rarity. Others are measured by who you share them with. This one was both.',
  },
  memories: [
    { tone: 'playful', text: 'Some songs are better left in the moment. Fortunately for everyone involved, no official karaoke recordings have surfaced.' },
  ],
  gallery: [],
  relatedYears: [2022, 2023, 2024],
  tags: ['reckon-ball', 'private-dining', 'gift-card-exchange', 'karaoke', 'signature-toast'],
};
